/**
 * entities/organization/api/queries — TanStack Query hooks for org endpoints.
 *
 * Typed against the generated schema. Components consume these hooks;
 * never call the raw apiClient directly from UI code.
 *
 * Exported query keys are stable constants so callers can invalidate them.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/shared/api';
import type { Organization, PaginatedOrganizationList, PaginatedMembershipList } from '../model/types';

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const MY_ORGS_QUERY_KEY = ['organizations', 'my-orgs'] as const;

export function orgMembersQueryKey(orgId: string) {
  return ['organizations', orgId, 'members'] as const;
}

// ---------------------------------------------------------------------------
// useMyOrgsQuery — GET /api/organizations/
// ---------------------------------------------------------------------------

/**
 * Returns the paginated list of organizations the authenticated user belongs to.
 */
export function useMyOrgsQuery() {
  return useQuery<PaginatedOrganizationList, Error>({
    queryKey: MY_ORGS_QUERY_KEY,
    queryFn: async (): Promise<PaginatedOrganizationList> => {
      const result = await (
        apiClient.GET as (
          p: string,
        ) => Promise<{ data?: unknown; error?: unknown; response: Response }>
      )('/api/organizations/');
      if (result.error !== undefined) {
        const err = result.error;
        const detail =
          typeof err === 'object' && err !== null && 'detail' in err
            ? String((err as Record<string, unknown>)['detail'])
            : `API error: ${result.response.status}`;
        throw new Error(detail);
      }
      return result.data as PaginatedOrganizationList;
    },
  });
}

// ---------------------------------------------------------------------------
// useOrgMembersQuery — GET /api/organizations/{id}/members/
// ---------------------------------------------------------------------------

/**
 * Returns the paginated list of memberships for the given organization.
 */
export function useOrgMembersQuery(orgId: string) {
  return useQuery<PaginatedMembershipList, Error>({
    queryKey: orgMembersQueryKey(orgId),
    queryFn: async (): Promise<PaginatedMembershipList> => {
      const result = await (
        apiClient.GET as (
          p: string,
          opts: { params: { path: { id: string } } },
        ) => Promise<{ data?: unknown; error?: unknown; response: Response }>
      )('/api/organizations/{id}/members/', { params: { path: { id: orgId } } });
      if (result.error !== undefined) {
        const err = result.error;
        const detail =
          typeof err === 'object' && err !== null && 'detail' in err
            ? String((err as Record<string, unknown>)['detail'])
            : `API error: ${result.response.status}`;
        throw new Error(detail);
      }
      return result.data as PaginatedMembershipList;
    },
    enabled: !!orgId,
  });
}

// ---------------------------------------------------------------------------
// useCreateOrgMutation — POST /api/organizations/
// ---------------------------------------------------------------------------

/**
 * Mutation hook for creating a new organization.
 * On success, invalidates the my-orgs query so the list refreshes.
 */
export function useCreateOrgMutation() {
  const queryClient = useQueryClient();

  return useMutation<Organization, Error, { name: string }>({
    mutationFn: async (body): Promise<Organization> => {
      const result = await (
        apiClient.POST as (
          p: string,
          opts: { body: { name: string } },
        ) => Promise<{ data?: unknown; error?: unknown; response: Response }>
      )('/api/organizations/', { body });
      if (result.error !== undefined) {
        const err = result.error;
        const detail =
          typeof err === 'object' && err !== null && 'detail' in err
            ? String((err as Record<string, unknown>)['detail'])
            : `API error: ${result.response.status}`;
        throw new Error(detail);
      }
      return result.data as Organization;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MY_ORGS_QUERY_KEY });
    },
  });
}

// ---------------------------------------------------------------------------
// useSetActiveOrgMutation — POST /api/organizations/set-active/
// ---------------------------------------------------------------------------

/**
 * Mutation hook for validating and activating an organization on the backend.
 * Returns the Organization object on success.
 * The caller (OrgContextProvider) updates the store after this resolves.
 */
export function useSetActiveOrgMutation() {
  return useMutation<Organization, Error, { organization_id: string }>({
    mutationFn: async (body): Promise<Organization> => {
      const result = await (
        apiClient.POST as (
          p: string,
          opts: { body: { organization_id: string } },
        ) => Promise<{ data?: unknown; error?: unknown; response: Response }>
      )('/api/organizations/set-active/', { body });
      if (result.error !== undefined) {
        const err = result.error;
        const detail =
          typeof err === 'object' && err !== null && 'detail' in err
            ? String((err as Record<string, unknown>)['detail'])
            : `API error: ${result.response.status}`;
        throw new Error(detail);
      }
      return result.data as Organization;
    },
  });
}
